import {
  Badge,
  Button,
  Card,
  Checkbox,
  DataTable,
  EmptyState,
  Layout,
  Modal,
  Page,
  Pagination,
  Spinner,
  Text,
  Toast,
} from '@shopify/polaris'
import { useCallback, useEffect, useState } from 'react'

import Avatar from '../components/Avatar'
import FormattedDateTime from '../components/FormattedDateTime'
import PermissionBadge from '../components/PermissionBadge'
import { useAdminApi } from '../hooks/useAdminApi'
import { AdminUser, useAdminAuth } from '../providers/AdminAuthProvider'

interface AdminsResponse {
  admins: AdminUser[]
  total: number
  page: number
  limit: number
  total_pages: number
}

export default function AdminAccountsPage() {
  const { listAdmins, updateAdminStatus, updateAdminPermissions } =
    useAdminApi()
  const { hasPermission } = useAdminAuth()
  const [admins, setAdmins] = useState<AdminUser[]>([])
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [toastMessage, setToastMessage] = useState('')
  const [toastError, setToastError] = useState(false)
  const [updatingAdmin, setUpdatingAdmin] = useState<string | null>(null)
  const [permissionModalOpen, setPermissionModalOpen] = useState(false)
  const [selectedAdminForPermissions, setSelectedAdminForPermissions] =
    useState<AdminUser | null>(null)
  const [tempPermissions, setTempPermissions] = useState<string[]>([])

  const canManageAdmins = hasPermission('manage_admin')

  const availablePermissions = [
    { key: 'view_dashboard', label: 'View Dashboard' },
    { key: 'view_shops', label: 'View Shops' },
    { key: 'login_shops', label: 'Login to Shops' },
    { key: 'view_admin', label: 'View Admin Accounts' },
    { key: 'manage_admin', label: 'Manage Admin Accounts' },
  ]

  const fetchAdmins = useCallback(async () => {
    setLoading(true)
    const params = {
      page: currentPage,
      limit: 20,
    }

    const response = await listAdmins(params)
    if (response.success && response.data) {
      const data = response.data as AdminsResponse
      setAdmins(data.admins)
      setTotalPages(data.total_pages)
    } else {
      showToast('Failed to load admin accounts', true)
    }
    setLoading(false)
  }, [listAdmins, currentPage])

  useEffect(() => {
    fetchAdmins()
  }, [fetchAdmins])

  const showToast = (message: string, isError = false) => {
    setToastMessage(message)
    setToastError(isError)
  }

  const handleStatusToggle = async (admin: AdminUser) => {
    const newStatus = admin.status === 'active' ? 'inactive' : 'active'
    setUpdatingAdmin(admin.id)

    const response = await updateAdminStatus(admin.id, newStatus)

    if (response.success) {
      // Update local state
      setAdmins(prevAdmins =>
        prevAdmins.map(a =>
          a.id === admin.id ? { ...a, status: newStatus } : a
        )
      )
      showToast(
        `Admin account ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`
      )
    } else {
      showToast(response.error || 'Failed to update admin status', true)
    }

    setUpdatingAdmin(null)
  }

  const handlePermissionsClick = (admin: AdminUser) => {
    setSelectedAdminForPermissions(admin)
    setTempPermissions([...admin.permissions])
    setPermissionModalOpen(true)
  }

  const handlePermissionToggle = (permission: string) => {
    setTempPermissions(prev =>
      prev.includes(permission)
        ? prev.filter(p => p !== permission)
        : [...prev, permission]
    )
  }

  const handleSavePermissions = async () => {
    if (!selectedAdminForPermissions) return

    setLoading(true)
    const response = await updateAdminPermissions(
      selectedAdminForPermissions.id,
      tempPermissions
    )

    if (response.success) {
      // Update local state
      setAdmins(prevAdmins =>
        prevAdmins.map(a =>
          a.id === selectedAdminForPermissions.id
            ? { ...a, permissions: [...tempPermissions] }
            : a
        )
      )
      showToast('Admin permissions updated successfully')
      setPermissionModalOpen(false)
      setSelectedAdminForPermissions(null)
    } else {
      showToast(response.error || 'Failed to update admin permissions', true)
    }

    setLoading(false)
  }

  const handleCancelPermissions = () => {
    setPermissionModalOpen(false)
    setSelectedAdminForPermissions(null)
    setTempPermissions([])
  }

  const getPermissionBadges = (permissions: string[]) => {
    return permissions
      .sort()
      .map(permission => (
        <PermissionBadge key={permission} permission={permission} />
      ))
  }

  const rows = admins.map(admin => [
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <Avatar name={admin.name} picture={admin.picture} size={'md'} />
      <div>
        <Text as="p" variant="bodyMd" fontWeight="semibold">
          {admin.name}
        </Text>
        <Text as="p" variant="bodySm" tone="subdued">
          {admin.id}
        </Text>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <Badge tone={admin.status === 'active' ? 'success' : 'critical'}>
            {admin.status === 'active' ? 'Active' : 'Inactive'}
          </Badge>
          <Button
            size="micro"
            variant={admin.status === 'active' ? 'secondary' : 'primary'}
            tone={admin.status === 'active' ? 'critical' : 'success'}
            loading={updatingAdmin === admin.id}
            disabled={!canManageAdmins}
            onClick={() => handleStatusToggle(admin)}
          >
            {admin.status === 'active' ? 'Deactivate' : 'Activate'}
          </Button>
        </div>
      </div>
    </div>,
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        alignItems: 'flex-start',
      }}
    >
      <Button
        size="micro"
        disabled={!canManageAdmins}
        onClick={() => handlePermissionsClick(admin)}
      >
        Edit Permissions
      </Button>
      {getPermissionBadges(admin.permissions)}
    </div>,
    <FormattedDateTime date={admin.created_at} />,
    <FormattedDateTime date={admin.last_login_at} fallback="Never" />,
  ])

  const toast = toastMessage ? (
    <Toast
      content={toastMessage}
      error={toastError}
      onDismiss={() => setToastMessage('')}
    />
  ) : null

  return (
    <>
      {toast}
      <Page title="Admin Accounts">
        <Layout>
          <Layout.Section>
            <Card>
              {loading ? (
                <div style={{ padding: '32px', textAlign: 'center' }}>
                  <Spinner size="large" />
                </div>
              ) : admins.length === 0 ? (
                <EmptyState heading="No admin accounts found" image="">
                  <p>No admin accounts are currently configured.</p>
                </EmptyState>
              ) : (
                <>
                  <DataTable
                    columnContentTypes={['text', 'text', 'text', 'text']}
                    headings={['Admin', 'Permissions', 'Created', 'Last Login']}
                    rows={rows}
                  />

                  {totalPages > 1 && (
                    <div
                      style={{
                        padding: '16px',
                        display: 'flex',
                        justifyContent: 'center',
                      }}
                    >
                      <Pagination
                        hasPrevious={currentPage > 1}
                        onPrevious={() => setCurrentPage(currentPage - 1)}
                        hasNext={currentPage < totalPages}
                        onNext={() => setCurrentPage(currentPage + 1)}
                      />
                    </div>
                  )}
                </>
              )}
            </Card>
          </Layout.Section>
        </Layout>

        {/* Permissions Modal */}
        <Modal
          open={permissionModalOpen}
          onClose={handleCancelPermissions}
          title={`Edit Permissions - ${selectedAdminForPermissions?.name}`}
          primaryAction={{
            content: 'Save Changes',
            onAction: handleSavePermissions,
            loading: loading,
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: handleCancelPermissions,
            },
          ]}
        >
          <Modal.Section>
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
            >
              <Text as="p" variant="bodyMd">
                Select the permissions for {selectedAdminForPermissions?.name}:
              </Text>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                }}
              >
                {availablePermissions.map(permission => (
                  <Checkbox
                    key={permission.key}
                    label={permission.label}
                    checked={tempPermissions.includes(permission.key)}
                    onChange={() => handlePermissionToggle(permission.key)}
                  />
                ))}
              </div>
            </div>
          </Modal.Section>
        </Modal>
      </Page>
    </>
  )
}
